import React, { useState, useCallback, useEffect } from "react";
import { Tabs, Tab, Modal, Button } from "react-bootstrap";
import CommonFormLabel from "../../components/formLabel";
import Input from "../../components/input";
import ValidationFeedback from "../../components/validationFeedback";
import CommonButton from "../../components/commonButton";
import FileInput from "../../components/fileInput";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useLoader } from "../../context/LoaderContext";
import { Card, Toast, Form, Row, Col, Container } from "react-bootstrap";
import { Formik } from "formik";
import { updatePage, getPageDetails } from "./pageSlice";
import {
  customerCareSchema,
  locationSchema,
  locationUpdateSchema,
  pageUpdateSchema,
} from "../../schema/validationSchemas";
import Editor from "../../components/commonEditor";
import { TaskEdit02Icon } from "../../assets/icons";
import MetaFormCard from "../../components/metaContents/MetaFormCard";
import { getSeoContents, updateSeoContent } from "../Pages/seoContentSlice";
import HomePage from "../Pages/HomePage/HomePageList";
import { getSettings, updateCustomerCareData } from "../Settings/settingSlice";
import TextArea from "../../components/commonTextArea";
import { addLocation, getLocations, makePrimaryStatus, updateLocation } from "../Pages/locationSlice";
import CommonTable from '../../components/commonTable/index';

export default function Edit() {
  const { id: pageId } = useParams();
  const { setLoading } = useLoader();
  const dispatch = useDispatch();
  const { settings } = useSelector((state) => state.settings);

  const [page, setPage] = useState(null);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarVariant, setSnackbarVariant] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [seoData, setSeoData] = useState(null);
  const [editingSeo, setEditingSeo] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [selectedLocationData, setSelectedLocationData] = useState({});
  const [showStatusModal, setShowStatusModal] = useState(false);
  

  const { locations } = useSelector((state) => state.locations);

  const locationColumns = [
    { label: 'Branch Name', key: 'branch_name' },
    { label: 'Email', key: 'email' },
    { label: 'Alt Email', key: 'alt_email' },
    { label: 'Phone Number', key: 'phone_number' },
    { label: 'Alt Phone Number', key: 'alt_phone_number' },
    { label: 'Whatsapp Number', key: 'whatsapp_number' },
    // { label: 'created at', key: 'created_at', type: 'date' },
    // {label: 'Status', key: 'is_active', type: 'status' },
    {label: 'Is Primary', key: 'is_primary', type: 'primary' },
  ];


  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleEditLocation = (row) => {
    setSelectedLocationId(row.id);
    setSelectedLocationData(row);
    setEditingSection('location');
  }

  const fetchSeoData = useCallback(
    async (slug) => {
      try {
        const res = await dispatch(
          getSeoContents({
            payload: { slug},
          })
        );
        if (res.payload?.data) setSeoData(res.payload.data);
      } catch (error) {
        console.error("Error fetching SEO content:", error);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (page?.slug) {
      fetchSeoData(page.slug);
    }
  }, [page?.slug, fetchSeoData]);

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        await dispatch(getSettings());
      } catch (error) {
        setLoading(false);
        console.error("Error fetching settings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [dispatch, setLoading]);

  const fetchSettingsData = async () => {
    setLoading(true);
    try {
      await dispatch(getSettings());
    } catch (error) {
      setLoading(false);
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCustomerCareData = async (
    values,
    { setErrors, setSubmitting }
  ) => {
    try {
      // setLoading(true);
      setIsSubmitting(true);
      setSubmitting(true);
      const res = await dispatch(updateCustomerCareData(values));
      if (res.payload.success === true) {
        setSnackbarMessage(
          res.payload.message || "Customer care data updated successfully"
        );
        setSnackbarVariant("success");
        setEditingSection(null);
        setShowSnackbar(true);
        setIsSubmitting(false);
        setSubmitting(false);
        fetchSettingsData();
      } else {
        setErrors(res.payload.data);
        setSnackbarMessage(
          res.payload.message || "Failed to update customer care data"
        );
        setSnackbarVariant("danger");
        setShowSnackbar(true);
        setIsSubmitting(false);
        setSubmitting(false);
      }
    } catch (error) {
      setSnackbarMessage("Failed to update customer care data");
      setSnackbarVariant("danger");
      setShowSnackbar(true);
      setIsSubmitting(false);
      setSubmitting(false);
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
      // setLoading(false);
    }
  };

  const handleSeoSubmit = async (values, { setErrors, setSubmitting }) => {
    try {
      setSubmitting(true);
      const payload = {
        ...values,
      };
      const res = await dispatch(updateSeoContent(payload));

      if (res.payload?.success) {
        setSnackbarMessage(res.payload.message || "SEO updated");
        setSnackbarVariant("success");
        setEditingSeo(false);
        fetchSeoData(values.slug);
      } else {
        setErrors(res.payload?.data || {});
        setSnackbarMessage(res.payload?.message || "Failed to update SEO");
        setSnackbarVariant("danger");
      }
    } catch (err) {
      setSnackbarMessage("Error updating SEO");
      setSnackbarVariant("danger");
    } finally {
      setShowSnackbar(true);
      setSubmitting(false);
    }
  };

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const result = await dispatch(getPageDetails(pageId));
      localStorage.setItem(
        "admin-page-data",
        JSON.stringify(result.payload.data)
      );
      setPage(result.payload.data);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching page:", error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, pageId, setLoading]);

  useEffect(() => {
    setPage(null);
    if (pageId) {
      fetchPage();
    }
  }, [pageId, fetchPage]);

    useEffect(() => {
      const fetchLocations = async () => {
        setLoading(true);
        const payload = {
          // search: searchTerm,
        };
    
        try {
          await dispatch(getLocations({ payload }));
        } catch (error) {
          setLoading(false);
          console.error("Error fetching locations:", error);
        } finally {
          setLoading(false);
        }
      };
    
      fetchLocations();
    }, [dispatch, setLoading]);

  const handleCancel = () => {
    setIsEditMode(false); // Go back to view mode
    setNewImagePreview(null);
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    setSelectedLocationId('');
    setSelectedLocationData({});
  };

  const handleImageChange = (file, setFieldValue) => {
    if (file) {
      setNewImagePreview(URL.createObjectURL(file));
      setFieldValue("banner_image", file);
    }
  };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setIsSubmitting(true);
    const updatedValues = { ...values, id: pageId };
    const formData = new FormData();
    formData.append("id", updatedValues.id);
    formData.append("_method", "PUT");
    formData.append("title", updatedValues.title);
    formData.append("description", updatedValues.description);
    formData.append("banner_image", updatedValues.banner_image);

    await dispatch(updatePage(formData))
      .then((res) => {
        if (res.payload && res.payload.success) {
          localStorage.setItem("page_notify_message", res.payload.message);
          localStorage.setItem(
            "admin-page-data",
            JSON.stringify(updatedValues.title)
          );
          window.dispatchEvent(new Event("page-title-updated"));
          setSnackbarMessage(res.payload.message);
          setSnackbarVariant("success");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
          // setPage(null);
          setIsEditMode(false);
          fetchPage();
          // navigate(`/organization/${selected_organization_id}/page/${updatedValues.id}`);
        } else {
          setErrors(res.payload.data);
          setSnackbarMessage(res.payload.message || "Failed to update page!");
          setSnackbarVariant("danger");
          setShowSnackbar(true);
          setSubmitting(false);
          setIsSubmitting(false);
        }
      })
      .catch((error) => {
        setSnackbarMessage("Failed to update page!");
        setSnackbarVariant("danger");
        setShowSnackbar(true);
        setSubmitting(false);
        setIsSubmitting(false);
      });
  };

    const handleSaveNewLocation = async (values, { setErrors, setSubmitting }) => {
      try {
        // setLoading(true);
        setIsSubmitting(true);
        setSubmitting(true);
        const res = await dispatch(addLocation(values));
        if(res.payload.success === true) {
          setSnackbarMessage(res.payload.message || 'New Location added successfully');
          setSnackbarVariant('success');
          setEditingSection(null);
          setShowSnackbar(true);
          setIsSubmitting(false);
          setSubmitting(false);
          fetchSettingsData();
          const payload = {
            // search: searchTerm,
          };
          dispatch(getLocations({ payload }));
        } else {
          setErrors(res.payload.data);
          setSnackbarMessage(res.payload.message || 'Failed to add Location');
          setSnackbarVariant('danger');
          setShowSnackbar(true);
          setIsSubmitting(false);
          setSubmitting(false);
        }
      } catch (error) {
        setSnackbarMessage('Failed to add Location');
        setSnackbarVariant('danger');
        setShowSnackbar(true);
        setIsSubmitting(false);
        setSubmitting(false);
      } finally {
        setIsSubmitting(false);
        setSubmitting(false);
        // setLoading(false);
      }
    };
  
    const handleUpdateLocation = async (values, { setErrors, setSubmitting }) => {
      try {
        // setLoading(true);
        setIsSubmitting(true);
        setSubmitting(true);
        const formData = new FormData();
        formData.append("id", values.id);
        formData.append("_method", "PUT");
        formData.append("branch_name", values.branch_name);
        formData.append("address", values.address);
        formData.append("map_location", values.map_location);
        formData.append("phone_number", values.phone_number);
        formData.append("alt_phone_number", values.alt_phone_number);
        formData.append("email", values.email);
        formData.append("alt_email", values.alt_email);
        formData.append("whatsapp_number", values.whatsapp_number);
  
        const res = await dispatch(updateLocation(formData));
        if(res.payload.success === true) {
          setSnackbarMessage(res.payload.message || 'Location updated successfully');
          setSnackbarVariant('success');
          setEditingSection(null);
          setShowSnackbar(true);
          setIsSubmitting(false);
          setSubmitting(false);
          fetchSettingsData();
          const payload = {
            // search: searchTerm,
          };
          dispatch(getLocations({ payload }));
        } else {
          setErrors(res.payload.data);
          setSnackbarMessage(res.payload.message || 'Failed to update Location');
          setSnackbarVariant('danger');
          setShowSnackbar(true);
          setIsSubmitting(false);
          setSubmitting(false);
        }
      } catch (error) {
        setSnackbarMessage('Failed to update Location');
        setSnackbarVariant('danger');
        setShowSnackbar(true);
        setIsSubmitting(false);
        setSubmitting(false);
      } finally {
        setIsSubmitting(false);
        setSubmitting(false);
        // setLoading(false);
      }
    };

    const handleStatusChange = async (id) => {
      setSelectedLocationId(id);
      setShowStatusModal(true);
    }

      const handlePrimaryStatusChangeNew = async () => {
        await dispatch(makePrimaryStatus(selectedLocationId)).then((res) => {
          if(res.payload.success) {
            setShowStatusModal(false);
            setSnackbarMessage(res.payload.message);
            setSnackbarVariant("success");
            setShowSnackbar(true);
            const payload = {
              // search: searchTerm,
            };
            dispatch(getLocations({ payload }));
          } else {
            setShowStatusModal(false);
            setSnackbarMessage(res.payload.message);
            setSnackbarVariant("danger");
            setShowSnackbar(true);
          }
        });
      }

  if (!page)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <h2>Loading Page Details...</h2>
      </div>
    );

  return (
    <>
      <Toast
        onClose={() => setShowSnackbar(false)}
        show={showSnackbar}
        delay={3000}
        autohide
        bg={snackbarVariant}
        style={{ position: "fixed", top: 20, right: 20, zIndex: 1000 }}
      >
        <Toast.Body style={{ color: "white" }}>{snackbarMessage}</Toast.Body>
      </Toast>

      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Primary Update</Modal.Title>
          </Modal.Header>
          <Modal.Body>Are you sure you want to make primary this Address/Location?</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handlePrimaryStatusChangeNew}>
              Change
            </Button>
          </Modal.Footer>
        </Modal>

      <Container fluid className="px-0">
        <Tabs defaultActiveKey="basic" id="page-tabs" className="mb-3">
          <Tab eventKey="basic" title="Basic Details">
            {!isEditMode ? (
              <>
                {/* View Mode: Basic Information */}
                <Card className="mb-4">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Basic Information</h5>
                    <CommonButton
                      icon={
                        <TaskEdit02Icon
                          width={16}
                          height={16}
                          color="#ffffff"
                          strokeWidth="2"
                        />
                      }
                      title="Edit"
                      className="btn-primary primary-shadow"
                      onClick={handleEdit}
                    />
                  </Card.Header>
                  <Card.Body>
                    <Row className="gy-4">
                      <Col md={12}>
                        <CommonFormLabel
                          htmlFor="title"
                          title="Page Title"
                          style={{ fontWeight: "bold", fontSize: "16px" }}
                        />
                        <p>{page.title}</p>
                      </Col>
                      <Col md={12}>
                        <CommonFormLabel
                          htmlFor="description"
                          title="Description"
                          style={{ fontWeight: "bold", fontSize: "16px" }}
                        />
                        <p
                          dangerouslySetInnerHTML={{
                            __html: page?.description,
                          }}
                        />
                      </Col>
                      <Col md={12}>
                        <CommonFormLabel
                          htmlFor="banner_image"
                          title="Image"
                          style={{ fontWeight: "bold", fontSize: "16px" }}
                        />
                        <div className="mt-2">
                          <img
                            src={page.banner_image}
                            alt="BannerImage"
                            className="img-fluid"
                            height={"150px"}
                            width={"150px"}
                          />
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* View Mode: Customer Care Section */}
                {page.slug === "contact-us" && (
                  <>
                    <Card className="mb-4">
                      <Card.Header className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Customer Care</h5>
                        {editingSection !== "customer-care" && (
                          <CommonButton
                            icon={
                              <TaskEdit02Icon
                                width={16}
                                height={16}
                                color="#ffffff"
                                strokeWidth="2"
                              />
                            }
                            title="Edit"
                            className="btn-primary primary-shadow"
                            onClick={() => setEditingSection("customer-care")}
                          />
                        )}
                      </Card.Header>
                      <Card.Body>
                        {editingSection === "customer-care" ? (
                          <Formik
                            initialValues={{
                              customer_care_no:
                                settings?.customer_care_no || "",
                            }}
                            validationSchema={customerCareSchema}
                            onSubmit={handleSaveCustomerCareData}
                          >
                            {({
                              values,
                              handleChange,
                              handleSubmit,
                              touched,
                              errors,
                            }) => (
                              <form
                                onSubmit={handleSubmit}
                                encType="multipart/form-data"
                              >
                                <Row className="gy-4">
                                  <Col md={4}>
                                    <CommonFormLabel
                                      htmlFor="customer_care_no"
                                      title="Customer Care No"
                                    />
                                    <Input
                                      id="customer_care_no"
                                      type="text"
                                      name="customer_care_no"
                                      value={values.customer_care_no}
                                      onChange={handleChange}
                                      className={`shadow-none ${
                                        errors?.customer_care_no &&
                                        touched.customer_care_no
                                          ? "error"
                                          : "correct"
                                      }`}
                                    />
                                    {touched.customer_care_no &&
                                      errors?.customer_care_no && (
                                        <ValidationFeedback
                                          title={errors?.customer_care_no}
                                        />
                                      )}
                                  </Col>
                                  <Col
                                    md={12}
                                    className="d-flex justify-content-end gap-3 mt-4"
                                  >
                                    <CommonButton
                                      title={
                                        isSubmitting ? "Updating..." : "Update"
                                      }
                                      className="btn-primary primary-shadow"
                                      disabled={isSubmitting}
                                      type="submit"
                                    />
                                    <CommonButton
                                      title="Cancel"
                                      className="btn-muted"
                                      disabled={isSubmitting}
                                      type="button"
                                      onClick={handleCancelEdit}
                                    />
                                  </Col>
                                </Row>
                              </form>
                            )}
                          </Formik>
                        ) : (
                          <Row className="gy-4">
                            <Col md={6}>
                              <CommonFormLabel
                                htmlFor="customer_care_no"
                                title="Customer Care Number"
                                style={{ fontWeight: "bold", fontSize: "16px" }}
                              />
                              <p>{settings?.customer_care_no}</p>
                            </Col>
                          </Row>
                        )}
                      </Card.Body>
                    </Card>

                    <Card className="mb-4">
                      <Card.Header className="d-flex justify-content-between align-items-center">
                        <div className="d-flex justify-content-between align-items-center">
                          <h5 className="my-2">Branches / Locations</h5>
                          <CommonFormLabel
                            className={"ms-4 mt-2"}
                            htmlFor="note"
                            title="(Note: We will user 'Primary' contact detail to show Contact Us page in our website)"
                            style={{ fontSize: "16px" }}
                          />
                        </div>
                        {editingSection !== "location" && (
                          <CommonButton
                            icon={
                              <TaskEdit02Icon
                                width={16}
                                height={28}
                                color="#ffffff"
                                strokeWidth="2"
                              />
                            }
                            title="Add New"
                            className="btn-primary primary-shadow"
                            onClick={() => setEditingSection("location")}
                          />
                        )}
                      </Card.Header>
                      <Card.Body>
                        {editingSection === "location" ? (
                          <>
                            {selectedLocationId ? (
                              <>
                                <Formik
                                  initialValues={{
                                    branch_name:
                                      selectedLocationData?.branch_name || "",
                                    address:
                                      selectedLocationData?.address || "",
                                    map_location:
                                      selectedLocationData?.map_location || "",
                                    phone_number:
                                      selectedLocationData?.phone_number || "",
                                    alt_phone_number:
                                      selectedLocationData?.alt_phone_number ||
                                      "",
                                    email: selectedLocationData?.email || "",
                                    alt_email:
                                      selectedLocationData?.alt_email || "",
                                    whatsapp_number:
                                      selectedLocationData?.whatsapp_number ||
                                      "",
                                    id: selectedLocationData?.id || "",
                                  }}
                                  validationSchema={locationUpdateSchema}
                                  onSubmit={handleUpdateLocation}
                                >
                                  {({
                                    values,
                                    handleChange,
                                    handleSubmit,
                                    touched,
                                    errors,
                                    setFieldValue,
                                  }) => (
                                    <form onSubmit={handleSubmit}>
                                      <Row className="gy-4">
                                        <Col md={6}>
                                          <CommonFormLabel
                                            htmlFor="branch_name"
                                            title="Branch Name"
                                          />
                                          <Input
                                            id="branch_name"
                                            type="text"
                                            name="branch_name"
                                            value={values.branch_name}
                                            onChange={handleChange}
                                            className={`shadow-none ${
                                              errors?.branch_name &&
                                              touched.branch_name
                                                ? "error"
                                                : "correct"
                                            }`}
                                          />
                                          {touched.branch_name &&
                                            errors?.branch_name && (
                                              <ValidationFeedback
                                                title={errors?.branch_name}
                                              />
                                            )}
                                        </Col>

                                        <Col md={6}>
                                          <CommonFormLabel
                                            htmlFor="phone_number"
                                            title="Phone Number"
                                          />
                                          <Input
                                            id="phone_number"
                                            type="text"
                                            name="phone_number"
                                            value={values.phone_number}
                                            onChange={handleChange}
                                            className={`shadow-none ${
                                              errors?.phone_number &&
                                              touched.phone_number
                                                ? "error"
                                                : "correct"
                                            }`}
                                          />
                                          {touched.phone_number &&
                                            errors?.phone_number && (
                                              <ValidationFeedback
                                                title={errors?.phone_number}
                                              />
                                            )}
                                        </Col>

                                        <Col md={6}>
                                          <CommonFormLabel
                                            htmlFor="alt_phone_number"
                                            title="Alt Phone Number"
                                          />
                                          <Input
                                            id="alt_phone_number"
                                            type="text"
                                            name="alt_phone_number"
                                            value={values.alt_phone_number}
                                            onChange={handleChange}
                                            className={`shadow-none ${
                                              errors?.alt_phone_number &&
                                              touched.alt_phone_number
                                                ? "error"
                                                : "correct"
                                            }`}
                                          />
                                          {touched.alt_phone_number &&
                                            errors?.alt_phone_number && (
                                              <ValidationFeedback
                                                title={errors?.alt_phone_number}
                                              />
                                            )}
                                        </Col>

                                        <Col md={6}>
                                          <CommonFormLabel
                                            htmlFor="whatsapp_number"
                                            title="Whatsapp Number"
                                          />
                                          <Input
                                            id="whatsapp_number"
                                            type="text"
                                            name="whatsapp_number"
                                            value={values.whatsapp_number}
                                            onChange={handleChange}
                                            className={`shadow-none ${
                                              errors?.whatsapp_number &&
                                              touched.whatsapp_number
                                                ? "error"
                                                : "correct"
                                            }`}
                                          />
                                          {touched.whatsapp_number &&
                                            errors?.whatsapp_number && (
                                              <ValidationFeedback
                                                title={errors?.whatsapp_number}
                                              />
                                            )}
                                        </Col>

                                        <Col md={6}>
                                          <CommonFormLabel
                                            htmlFor="email"
                                            title="Email"
                                          />
                                          <Input
                                            id="email"
                                            type="email"
                                            name="email"
                                            value={values.email}
                                            onChange={handleChange}
                                            className={`shadow-none ${
                                              errors?.email && touched.email
                                                ? "error"
                                                : "correct"
                                            }`}
                                          />
                                          {touched.email && errors?.email && (
                                            <ValidationFeedback
                                              title={errors?.email}
                                            />
                                          )}
                                        </Col>

                                        <Col md={6}>
                                          <CommonFormLabel
                                            htmlFor="alt_email"
                                            title="Alt Email"
                                          />
                                          <Input
                                            id="alt_email"
                                            type="email"
                                            name="alt_email"
                                            value={values.alt_email}
                                            onChange={handleChange}
                                            className={`shadow-none ${
                                              errors?.alt_email &&
                                              touched.alt_email
                                                ? "error"
                                                : "correct"
                                            }`}
                                          />
                                          {touched.alt_email &&
                                            errors?.alt_email && (
                                              <ValidationFeedback
                                                title={errors?.alt_email}
                                              />
                                            )}
                                        </Col>

                                        <Col md={12} className="mb-4">
                                          <CommonFormLabel
                                            htmlFor="address"
                                            title="Address"
                                          />
                                          <TextArea
                                            id="address"
                                            name="address"
                                            placeholder="Enter Address"
                                            onChange={handleChange}
                                            value={values.address}
                                            className={`shadow-none ${
                                              errors?.address && touched.address
                                                ? "error"
                                                : "correct"
                                            }`}
                                            rows={6}
                                          />
                                          {touched.address &&
                                            errors?.address && (
                                              <ValidationFeedback
                                                title={errors?.address}
                                              />
                                            )}
                                        </Col>

                                        <Col md={12} className="mb-4">
                                          <CommonFormLabel
                                            htmlFor="map_location"
                                            title="Map Location"
                                          />
                                          <TextArea
                                            id="map_location"
                                            name="map_location"
                                            placeholder="Enter Map Location"
                                            onChange={handleChange}
                                            value={values.map_location}
                                            className={`shadow-none ${
                                              errors?.map_location &&
                                              touched.map_location
                                                ? "error"
                                                : "correct"
                                            }`}
                                            rows={6}
                                          />
                                          {touched.map_location &&
                                            errors?.map_location && (
                                              <ValidationFeedback
                                                title={errors?.map_location}
                                              />
                                            )}
                                        </Col>

                                        <Col
                                          md={12}
                                          className="d-flex justify-content-end gap-3 mt-4"
                                        >
                                          <CommonButton
                                            title={
                                              isSubmitting
                                                ? "Updating..."
                                                : "Update"
                                            }
                                            className="btn-primary primary-shadow"
                                            disabled={isSubmitting}
                                            type="submit"
                                          />
                                          <CommonButton
                                            title="Cancel"
                                            className="btn-muted"
                                            disabled={isSubmitting}
                                            type="button"
                                            onClick={handleCancelEdit}
                                          />
                                        </Col>
                                      </Row>
                                    </form>
                                  )}
                                </Formik>
                              </>
                            ) : (
                              <Formik
                                initialValues={{
                                  branch_name: "",
                                  address: "",
                                  map_location: "",
                                  phone_number: "",
                                  alt_phone_number: "",
                                  email: "",
                                  alt_email: "",
                                  whatsapp_number: "",
                                }}
                                validationSchema={locationSchema}
                                onSubmit={handleSaveNewLocation}
                              >
                                {({
                                  values,
                                  handleChange,
                                  handleSubmit,
                                  touched,
                                  errors,
                                  setFieldValue,
                                }) => (
                                  <form onSubmit={handleSubmit}>
                                    <Row className="gy-4">
                                      <Col md={6}>
                                        <CommonFormLabel
                                          htmlFor="branch_name"
                                          title="Branch Name"
                                        />
                                        <Input
                                          id="branch_name"
                                          type="text"
                                          name="branch_name"
                                          value={values.branch_name}
                                          onChange={handleChange}
                                          className={`shadow-none ${
                                            errors?.branch_name &&
                                            touched.branch_name
                                              ? "error"
                                              : "correct"
                                          }`}
                                        />
                                        {touched.branch_name &&
                                          errors?.branch_name && (
                                            <ValidationFeedback
                                              title={errors?.branch_name}
                                            />
                                          )}
                                      </Col>

                                      <Col md={6}>
                                        <CommonFormLabel
                                          htmlFor="phone_number"
                                          title="Phone Number"
                                        />
                                        <Input
                                          id="phone_number"
                                          type="text"
                                          name="phone_number"
                                          value={values.phone_number}
                                          onChange={handleChange}
                                          className={`shadow-none ${
                                            errors?.phone_number &&
                                            touched.phone_number
                                              ? "error"
                                              : "correct"
                                          }`}
                                        />
                                        {touched.phone_number &&
                                          errors?.phone_number && (
                                            <ValidationFeedback
                                              title={errors?.phone_number}
                                            />
                                          )}
                                      </Col>

                                      <Col md={6}>
                                        <CommonFormLabel
                                          htmlFor="alt_phone_number"
                                          title="Alt Phone Number"
                                        />
                                        <Input
                                          id="alt_phone_number"
                                          type="text"
                                          name="alt_phone_number"
                                          value={values.alt_phone_number}
                                          onChange={handleChange}
                                          className={`shadow-none ${
                                            errors?.alt_phone_number &&
                                            touched.alt_phone_number
                                              ? "error"
                                              : "correct"
                                          }`}
                                        />
                                        {touched.alt_phone_number &&
                                          errors?.alt_phone_number && (
                                            <ValidationFeedback
                                              title={errors?.alt_phone_number}
                                            />
                                          )}
                                      </Col>

                                      <Col md={6}>
                                        <CommonFormLabel
                                          htmlFor="whatsapp_number"
                                          title="Whatsapp Number"
                                        />
                                        <Input
                                          id="whatsapp_number"
                                          type="text"
                                          name="whatsapp_number"
                                          value={values.whatsapp_number}
                                          onChange={handleChange}
                                          className={`shadow-none ${
                                            errors?.whatsapp_number &&
                                            touched.whatsapp_number
                                              ? "error"
                                              : "correct"
                                          }`}
                                        />
                                        {touched.whatsapp_number &&
                                          errors?.whatsapp_number && (
                                            <ValidationFeedback
                                              title={errors?.whatsapp_number}
                                            />
                                          )}
                                      </Col>

                                      <Col md={6}>
                                        <CommonFormLabel
                                          htmlFor="email"
                                          title="Email"
                                        />
                                        <Input
                                          id="email"
                                          type="email"
                                          name="email"
                                          value={values.email}
                                          onChange={handleChange}
                                          className={`shadow-none ${
                                            errors?.email && touched.email
                                              ? "error"
                                              : "correct"
                                          }`}
                                        />
                                        {touched.email && errors?.email && (
                                          <ValidationFeedback
                                            title={errors?.email}
                                          />
                                        )}
                                      </Col>

                                      <Col md={6}>
                                        <CommonFormLabel
                                          htmlFor="alt_email"
                                          title="Alt Email"
                                        />
                                        <Input
                                          id="alt_email"
                                          type="email"
                                          name="alt_email"
                                          value={values.alt_email}
                                          onChange={handleChange}
                                          className={`shadow-none ${
                                            errors?.alt_email &&
                                            touched.alt_email
                                              ? "error"
                                              : "correct"
                                          }`}
                                        />
                                        {touched.alt_email &&
                                          errors?.alt_email && (
                                            <ValidationFeedback
                                              title={errors?.alt_email}
                                            />
                                          )}
                                      </Col>

                                      <Col md={12} className="mb-4">
                                        <CommonFormLabel
                                          htmlFor="address"
                                          title="Address"
                                        />
                                        <TextArea
                                          id="address"
                                          name="address"
                                          placeholder="Enter Address"
                                          onChange={handleChange}
                                          value={values.address}
                                          className={`shadow-none ${
                                            errors?.address && touched.address
                                              ? "error"
                                              : "correct"
                                          }`}
                                          rows={6}
                                        />
                                        {touched.address && errors?.address && (
                                          <ValidationFeedback
                                            title={errors?.address}
                                          />
                                        )}
                                      </Col>

                                      <Col md={12} className="mb-4">
                                        <CommonFormLabel
                                          htmlFor="map_location"
                                          title="Map Location"
                                        />
                                        <TextArea
                                          id="map_location"
                                          name="map_location"
                                          placeholder="Enter Map Location"
                                          onChange={handleChange}
                                          value={values.map_location}
                                          className={`shadow-none ${
                                            errors?.map_location &&
                                            touched.map_location
                                              ? "error"
                                              : "correct"
                                          }`}
                                          rows={6}
                                        />
                                        {touched.map_location &&
                                          errors?.map_location && (
                                            <ValidationFeedback
                                              title={errors?.map_location}
                                            />
                                          )}
                                      </Col>

                                      <Col
                                        md={12}
                                        className="d-flex justify-content-end gap-3 mt-4"
                                      >
                                        <CommonButton
                                          title={
                                            isSubmitting ? "Adding..." : "Add"
                                          }
                                          className="btn-primary primary-shadow"
                                          disabled={isSubmitting}
                                          type="submit"
                                        />
                                        <CommonButton
                                          title="Cancel"
                                          className="btn-muted"
                                          disabled={isSubmitting}
                                          type="button"
                                          onClick={handleCancelEdit}
                                        />
                                      </Col>
                                    </Row>
                                  </form>
                                )}
                              </Formik>
                            )}
                          </>
                        ) : (
                          <div className="bg-white rounded-2 overflow-hidden common-shadow">
                            {locations && locations?.data?.length > 0 ? (
                              <CommonTable
                                columns={locationColumns}
                                data={locations?.data || []}
                                showActions={true}
                                actions={{
                                  showEdit: true,
                                  showDelete: false,
                                  showView: false,
                                  onEdit: handleEditLocation,
                                  // onDelete: handleDelete,
                                  // onView: handleView
                                }}
                                onStatusChange={handleStatusChange}
                              />
                            ) : (
                              <>
                                <div className="bg-white rounded-2 overflow-hidden common-shadow">
                                  <h3 className="text-center my-5">
                                    No Locations Found
                                  </h3>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </>
                )}
              </>
            ) : (
              <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Basic Information</h5>
                </Card.Header>
                <Card.Body>
                  <Formik
                    initialValues={{
                      title: page.title || "",
                      description: page.description || "",
                      banner_image: "",
                    }}
                    validationSchema={pageUpdateSchema}
                    onSubmit={handleSubmit}
                  >
                    {({
                      errors,
                      touched,
                      handleChange,
                      handleSubmit,
                      values,
                      setFieldValue,
                    }) => (
                      <Form
                        onSubmit={handleSubmit}
                        encType="multipart/form-data"
                        className="bg-white p-4 rounded-2 shadow-sm"
                      >
                        <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
                          <h4 className="mb-4 d-flex justify-content-center">
                            Basic
                          </h4>
                          <Row>
                            <Col md={12} className="mb-4">
                              <CommonFormLabel
                                htmlFor="title"
                                title="Page Title"
                              />
                              <Input
                                id="title"
                                name="title"
                                type="text"
                                placeholder="Enter Page Title"
                                onChange={handleChange}
                                value={values.title}
                                className={`shadow-none ${
                                  errors?.title && touched.title
                                    ? "error"
                                    : "correct"
                                }`}
                              />
                              {touched.title && errors?.title && (
                                <ValidationFeedback title={errors?.title} />
                              )}
                            </Col>

                            <div className="mb-4">
                              <CommonFormLabel
                                htmlFor="description"
                                title="Description"
                              />
                              <Editor
                                value={values.description}
                                onChange={(content) =>
                                  setFieldValue("description", content)
                                }
                              />
                              {touched.description && errors?.description && (
                                <ValidationFeedback
                                  title={errors?.description}
                                />
                              )}
                            </div>
                          </Row>
                        </Card>

                        <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
                          <h4 className="mb-4 d-flex justify-content-center">
                            Image
                          </h4>
                          <div className="mb-4">
                            <CommonFormLabel
                              htmlFor="banner_image"
                              title="Upload New Image"
                            />
                            <FileInput
                              name="banner_image"
                              onChange={(file) =>
                                handleImageChange(file, setFieldValue)
                              }
                              error={errors?.banner_image}
                              touched={touched.banner_image}
                            />
                            {touched.banner_image && errors?.banner_image && (
                              <ValidationFeedback
                                title={errors?.banner_image}
                              />
                            )}
                          </div>

                          <Row>
                            {newImagePreview && (
                              <Col md={6} className="">
                                <CommonFormLabel
                                  htmlFor="new_selected_image"
                                  title="New selected Image"
                                />
                                <div>
                                  <img
                                    src={newImagePreview}
                                    alt="New Selected"
                                    className="img-fluid rounded border"
                                    style={{ width: "200px", height: "auto" }}
                                  />
                                </div>
                              </Col>
                            )}

                            <Col md={6} className="">
                              <CommonFormLabel
                                htmlFor="banner_image"
                                title="Uploaded Image"
                              />
                              <div className="mt-2">
                                <img
                                  src={page.banner_image}
                                  alt="PageImage"
                                  className="img-fluid rounded border"
                                  style={{ width: "200px", height: "auto" }}
                                />
                              </div>
                            </Col>
                          </Row>
                        </Card>
                        <hr className="my-5" />

                        <div className="d-flex align-items-center justify-content-end gap-3 common-popup-btn">
                          <CommonButton
                            title={isSubmitting ? "Updating..." : "Update"}
                            type="submit"
                            disabled={isSubmitting}
                            className="btn-primary primary-shadow"
                          />
                          <CommonButton
                            title="Cancel"
                            onClick={handleCancel}
                            className="btn-muted"
                          />
                        </div>
                      </Form>
                    )}
                  </Formik>
                </Card.Body>
              </Card>
            )}
          </Tab>

          <Tab eventKey="seo" title="SEO Configuration">
              <MetaFormCard
                title={`${page?.title} Page Meta Tags`}
                slug={page?.slug}
                seoData={seoData}
                editingSection={editingSeo ? page?.slug : null}
                setEditingSection={(slug) => setEditingSeo(slug === page?.slug)}
                isSubmitting={isSubmitting}
                onSubmit={handleSeoSubmit}
                onCancel={() => setEditingSeo(false)}
              />
          </Tab>
          
          <Tab eventKey="section" title="Page Section">
            {page?.slug ? (
              <HomePage slug={page.slug} />
            ) : (
              <Card className="mb-4 text-center">
                <Card.Body>
                  <p>No content available</p>
                </Card.Body>
              </Card>
            )}
          </Tab>
        </Tabs>
      </Container>
    </>
  );
}
